import { ActionIcon, Group, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import type { RegistrationCourseCustomDataModelView } from '@actschurch/ccms-sdk';
import { parseChildren } from '../../../Components/ChildrenTab';

interface ChildrenReadOnlyProps {
  row: RegistrationCourseCustomDataModelView;
  onRefresh: () => void;
}

const label = (key: string) =>
  key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase());

const show = (v: unknown) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/**
 * Read-only list of the children captured on the registration.
 *
 * Used instead of the shared `ChildrenTab` because that component carries the add/edit/remove
 * controls and takes no read-only prop. Columns come from whatever keys the children carry, so this
 * does not depend on the exact child shape.
 */
export default function ChildrenReadOnly({ row, onRefresh }: ChildrenReadOnlyProps) {
  const children = parseChildren(row) as unknown as Record<string, unknown>[];
  const keys = Array.from(new Set(children.flatMap((c) => Object.keys(c ?? {}))));

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Tooltip label="Refresh children" withArrow>
          <ActionIcon variant="subtle" aria-label="Refresh children" onClick={onRefresh} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {children.length === 0 ? (
        <Text c="dimmed" size="sm">No children captured.</Text>
      ) : (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              {keys.map((k) => <Table.Th key={k}>{label(k)}</Table.Th>)}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {children.map((c, i) => (
              <Table.Tr key={i}>
                {keys.map((k) => <Table.Td key={k}>{show(c?.[k])}</Table.Td>)}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
